import { useState } from 'react';
import UserPicker from './UserPicker';
import GroupPicker from './GroupPicker';
import styles from './PrincipalAccessEditor.module.css';

const DEFAULT_LEVELS = [
    { value: 0, label: 'Read' },
    { value: 1, label: 'Write' },
    { value: 2, label: 'Admin' },
];

/**
 * Reusable editor for a list of users and groups with access levels.
 * Uses UserPicker and GroupPicker for search (no free-text IDs).
 * Suitable for folder/collection ACLs and any principal+level list.
 *
 * Props:
 *   value           — { users: [{ id, level }], groups: [{ id, level }] }
 *   onChange        — (value) => void
 *   displayNames    — { users: { [id]: string }, groups: { [id]: string } } optional
 *   levels          — optional array of { value, label } (default Read/Write/Admin)
 *   disabled        — if true, no add/remove or level change
 */
function PrincipalAccessEditor({
    value,
    onChange,
    displayNames = {},
    levels = DEFAULT_LEVELS,
    disabled = false,
}) {
    const users = value?.users ?? [];
    const groups = value?.groups ?? [];
    const userNames = displayNames?.users ?? {};
    const groupNames = displayNames?.groups ?? {};

    const [addingUser, setAddingUser] = useState(false);
    const [addingGroup, setAddingGroup] = useState(false);
    const [addLevel, setAddLevel] = useState(levels[0]?.value ?? 0);

    function removeUser(id) {
        if (disabled) return;
        onChange({
            ...value,
            users: users.filter((u) => String(u.id) !== String(id)),
        });
    }

    function removeGroup(id) {
        if (disabled) return;
        onChange({
            ...value,
            groups: groups.filter((g) => String(g.id) !== String(id)),
        });
    }

    function setUserLevel(id, level) {
        if (disabled) return;
        onChange({
            ...value,
            users: users.map((u) => (String(u.id) === String(id) ? { ...u, level } : u)),
        });
    }

    function setGroupLevel(id, level) {
        if (disabled) return;
        onChange({
            ...value,
            groups: groups.map((g) => (String(g.id) === String(id) ? { ...g, level } : g)),
        });
    }

    function handleSelectUser(user) {
        const id = user._id;
        if (users.some((u) => String(u.id) === String(id))) {
            setAddingUser(false);
            return;
        }
        onChange({
            ...value,
            users: [...users, { id, level: addLevel }],
        });
        setAddingUser(false);
    }

    function handleSelectGroup(group) {
        const id = group._id;
        if (groups.some((g) => String(g.id) === String(id))) {
            setAddingGroup(false);
            return;
        }
        onChange({
            ...value,
            groups: [...groups, { id, level: addLevel }],
        });
        setAddingGroup(false);
    }

    const excludeUserIds = users.map((u) => String(u.id));
    const excludeGroupIds = groups.map((g) => String(g.id));

    return (
        <div className={styles.root}>
            <div className={styles.section}>
                <span className={styles.sectionLabel}>Users</span>
                {users.length === 0 && !addingUser && (
                    <p className={styles.empty}>None</p>
                )}
                {users.map((u) => (
                    <div key={u.id} className={styles.row}>
                        <span className={styles.name}>{userNames[u.id] ?? u.id}</span>
                        {disabled ? (
                            <span className={styles.levelLabel}>
                                {levels.find((l) => l.value === u.level)?.label ?? u.level}
                            </span>
                        ) : (
                            <select
                                className={styles.levelSelect}
                                value={u.level}
                                onChange={(e) => setUserLevel(u.id, Number(e.target.value))}
                            >
                                {levels.map((l) => (
                                    <option key={l.value} value={l.value}>{l.label}</option>
                                ))}
                            </select>
                        )}
                        {!disabled && (
                            <button
                                type="button"
                                className={styles.removeBtn}
                                onClick={() => removeUser(u.id)}
                                title="Remove"
                            >
                                ✕
                            </button>
                        )}
                    </div>
                ))}
                {!disabled && (
                    <>
                        {addingUser ? (
                            <div className={styles.addRow}>
                                <UserPicker
                                    onSelect={handleSelectUser}
                                    excludeIds={excludeUserIds}
                                    placeholder="Search users…"
                                    autoFocus
                                />
                                <select
                                    className={styles.levelSelect}
                                    value={addLevel}
                                    onChange={(e) => setAddLevel(Number(e.target.value))}
                                >
                                    {levels.map((l) => (
                                        <option key={l.value} value={l.value}>{l.label}</option>
                                    ))}
                                </select>
                                <button
                                    type="button"
                                    className={styles.cancelAddBtn}
                                    onClick={() => setAddingUser(false)}
                                >
                                    Cancel
                                </button>
                            </div>
                        ) : (
                            <button
                                type="button"
                                className={styles.addButton}
                                onClick={() => setAddingUser(true)}
                            >
                                + Add user
                            </button>
                        )}
                    </>
                )}
            </div>

            <div className={styles.section}>
                <span className={styles.sectionLabel}>Groups</span>
                {groups.length === 0 && !addingGroup && (
                    <p className={styles.empty}>None</p>
                )}
                {groups.map((g) => (
                    <div key={g.id} className={styles.row}>
                        <span className={styles.name}>{groupNames[g.id] ?? g.id}</span>
                        {disabled ? (
                            <span className={styles.levelLabel}>
                                {levels.find((l) => l.value === g.level)?.label ?? g.level}
                            </span>
                        ) : (
                            <select
                                className={styles.levelSelect}
                                value={g.level}
                                onChange={(e) => setGroupLevel(g.id, Number(e.target.value))}
                            >
                                {levels.map((l) => (
                                    <option key={l.value} value={l.value}>{l.label}</option>
                                ))}
                            </select>
                        )}
                        {!disabled && (
                            <button
                                type="button"
                                className={styles.removeBtn}
                                onClick={() => removeGroup(g.id)}
                                title="Remove"
                            >
                                ✕
                            </button>
                        )}
                    </div>
                ))}
                {!disabled && (
                    <>
                        {addingGroup ? (
                            <div className={styles.addRow}>
                                <GroupPicker
                                    onSelect={handleSelectGroup}
                                    excludeIds={excludeGroupIds}
                                    placeholder="Search groups…"
                                    autoFocus
                                />
                                <select
                                    className={styles.levelSelect}
                                    value={addLevel}
                                    onChange={(e) => setAddLevel(Number(e.target.value))}
                                >
                                    {levels.map((l) => (
                                        <option key={l.value} value={l.value}>{l.label}</option>
                                    ))}
                                </select>
                                <button
                                    type="button"
                                    className={styles.cancelAddBtn}
                                    onClick={() => setAddingGroup(false)}
                                >
                                    Cancel
                                </button>
                            </div>
                        ) : (
                            <button
                                type="button"
                                className={styles.addButton}
                                onClick={() => setAddingGroup(true)}
                            >
                                + Add group
                            </button>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

export default PrincipalAccessEditor;
export { DEFAULT_LEVELS as ACCESS_LEVELS };
